import { Icon } from '@iconify/react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Card, Loader, SectionTitle } from '../../shared/components';
import { uploadFile } from '../../shared/service/fileUpload.service';
import AssignDialog from '../components/AssignDialog';
import { ColorChip, PriorityChip, StateChip } from '../components/Chips';
import PlanDialog from '../components/PlanDialog';
import ReassignDialog from '../components/ReassignDialog';
import TaskFormDialog from '../components/TaskFormDialog';
import TextPromptDialog from '../components/TextPromptDialog';
import TimeEntryDialog from '../components/TimeEntryDialog';
import TaskStatusTrail from '../components/TaskStatusTrail';
import { useAuth } from '../../auth/auth.context';
import { USER_ROLES } from '../../../config';
import EmployeePicker from '../components/EmployeePicker';
import DialogShell from '../components/DialogShell';
import { useTaskMeta } from '../components/useTaskMeta';
import { reassignmentsApi, tasksApi } from '../tasks.api';
import type { TaskDetail, TaskSummary } from '../tasks.types';
import { drawingsLabel, errorMessage, fmtDate, fmtDateTime, fmtHours, humanize } from '../tasks.utils';

type Prompt = {
  title: string;
  message?: string;
  label?: string;
  required?: boolean;
  submitLabel?: string;
  submitColor?: 'primary' | 'error' | 'warning' | 'success';
  run: (text: string) => Promise<unknown>;
  /** The action navigates away, so there is nothing to reload. */
  leaves?: boolean;
};

const ACTIVITY_ICONS: Record<string, string> = {
  TASK_CREATED: 'mdi:plus-circle-outline',
  TASK_ASSIGNED: 'mdi:account-arrow-right-outline',
  TASK_ACCEPTED: 'mdi:check-circle-outline',
  TASK_DECLINED: 'mdi:close-circle-outline',
  TASK_STARTED: 'mdi:play-circle-outline',
  TASK_RESUMED: 'mdi:play-circle-outline',
  TASK_PAUSED: 'mdi:pause-circle-outline',
  TASK_STOPPED: 'mdi:stop-circle-outline',
  TIME_LOGGED: 'mdi:clock-outline',
  TASK_RELEASE_REQUESTED: 'mdi:hand-back-left-outline',
  TASK_RELEASED: 'mdi:account-arrow-left-outline',
  TASK_REASSIGNED: 'mdi:account-switch-outline',
  TASK_REASSIGNMENT_APPROVED: 'mdi:thumb-up-outline',
  TASK_REASSIGNMENT_REJECTED: 'mdi:thumb-down-outline',
  TASK_BLOCKED: 'mdi:block-helper',
  TASK_COMPLETED: 'mdi:flag-checkered',
  TASK_CANCELLED: 'mdi:cancel',
  COMMENT_ADDED: 'mdi:comment-outline',
  ATTACHMENT_ADDED: 'mdi:paperclip',
  DUE_DATE_CHANGED: 'mdi:calendar-edit',
  PRIORITY_CHANGED: 'mdi:flag-outline',
};

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box>
      <Typography variant='caption' color='text.secondary'>
        {label}
      </Typography>
      <Typography component='div' fontWeight={500}>
        {children ?? '—'}
      </Typography>
    </Box>
  );
}

function SubtaskRow({ t }: { t: TaskSummary }) {
  return (
    <TableRow>
      <TableCell>
        <Link component={RouterLink} to={`/tasks/${t.id}`}>
          {t.taskCode}
        </Link>{' '}
        {t.title}
      </TableCell>
      <TableCell>{t.assigneeName ?? '—'}</TableCell>
      <TableCell>
        <ColorChip label={t.statusLabel} color={t.statusColor} />
      </TableCell>
      <TableCell>{fmtDate(t.dueDate)}</TableCell>
      <TableCell align='right'>
        {fmtHours(t.actualHours)} / {fmtHours(t.allocatedHours)}
      </TableCell>
    </TableRow>
  );
}

/** Everything about one task, and every action the caller may take on it. */
export default function TaskDetailPage() {
  const { id } = useParams();
  const taskId = Number(id);
  const navigate = useNavigate();
  const meta = useTaskMeta();
  /**
   * An employee sees the task as its worker: the trail, the facts, their time, comments and
   * files. Who held it, hand-overs and the day-by-day plan are the analyst's view.
   */
  const { role } = useAuth();
  const isEmployee = role === USER_ROLES.EMPLOYEE;
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [d, setD] = useState<TaskDetail | null>(null);
  const [tab, setTab] = useState(0);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState<null | 'edit' | 'subtask' | 'assign' | 'reassign' | 'approve' | 'plan' | 'time' | 'release' | 'dependency'>(null);
  const [prompt, setPrompt] = useState<Prompt | null>(null);
  const [statusMenu, setStatusMenu] = useState<HTMLElement | null>(null);
  const [comment, setComment] = useState('');
  const [releaseTo, setReleaseTo] = useState<number | null>(null);
  const [releaseReason, setReleaseReason] = useState('');
  const [dep, setDep] = useState({ related: '', type: 'BLOCKED_BY' });

  const load = useCallback(async () => {
    try {
      setD(await tasksApi.get(taskId));
    } catch (e) {
      toast.error(errorMessage(e, 'Failed to load the task'));
    }
  }, [taskId]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (fn: () => Promise<unknown>, ok: string, reload = true) => {
    setBusy(true);
    try {
      await fn();
      toast.success(ok);
      if (reload) await load();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const running = useMemo(
    () => d?.timeEntries.find((e) => e.running && e.employeeId === meta?.myEmployeeId) ?? null,
    [d, meta]
  );

  if (!d || !meta) return <Loader />;
  const t = d.task;
  const can = d.can;
  const pending = d.pendingReassignment;
  const offeredToMe = pending?.status === 'APPROVED' && pending.toEmployee?.id === meta.myEmployeeId;
  const current = d.currentAssignment;

  type StatusOption = { code: string; label: string; needs?: 'reason' };
  const statusOptions: StatusOption[] = [
    { code: 'IN_PROGRESS', label: 'In progress' },
    { code: 'ON_HOLD', label: 'On hold' },
    { code: 'BLOCKED', label: 'Blocked…', needs: 'reason' as const },
    { code: 'SUBMITTED', label: 'Submit for review' },
    ...(can.edit ? [{ code: 'REVISION_REQUESTED', label: 'Request revision' }] : []),
    ...(can.complete ? [{ code: 'COMPLETED', label: 'Complete' }] : []),
    ...(can.cancel ? [{ code: 'CANCELLED', label: 'Cancel task…', needs: 'reason' as const }] : []),
  ].filter((o) => o.code !== t.status);

  const chooseStatus = (o: (typeof statusOptions)[number]) => {
    setStatusMenu(null);
    // Submitting hands the work over for review, so it is asked about first.
    if (o.code === 'SUBMITTED') {
      setConfirmSubmit(true);
      return;
    }
    if (o.needs === 'reason') {
      setPrompt({
        title: o.code === 'CANCELLED' ? 'CANCEL TASK' : 'MARK BLOCKED',
        message:
          o.code === 'CANCELLED'
            ? 'Cancelling ends the work itself (unlike a release, which only ends one person’s ownership). Open subtasks are cancelled too.'
            : undefined,
        label: o.code === 'CANCELLED' ? 'Reason for cancelling' : 'What is it blocked on?',
        required: true,
        submitColor: o.code === 'CANCELLED' ? 'error' : 'warning',
        submitLabel: o.code === 'CANCELLED' ? 'Cancel task' : 'Mark blocked',
        run: (text) => tasksApi.changeStatus(taskId, o.code, text),
      });
    } else {
      act(() => tasksApi.changeStatus(taskId, o.code), `Status set to ${o.label.toLowerCase()}`);
    }
  };

  const reassignContext = {
    taskId,
    taskCode: t.taskCode,
    taskTitle: t.title,
    currentEmployeeId: current?.employeeId ?? null,
    currentEmployeeName: current?.employeeName ?? null,
    allocated: current?.allocatedHours ?? 0,
    actual: current?.actualHours ?? 0,
    remaining: current?.remainingHours ?? 0,
  };

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    await act(async () => {
      const up = await uploadFile(file);
      await tasksApi.attach(taskId, Number(up.id));
    }, 'File attached');
  };

  const allTabs: { label: string; count?: number; body: ReactNode }[] = [
    {
      label: 'Assignments',
      count: d.assignments.length,
      body: (
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Employee</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Assigned</TableCell>
              <TableCell>Ended</TableCell>
              <TableCell align='right'>Allocated</TableCell>
              <TableCell align='right'>Actual</TableCell>
              <TableCell align='right'>Released</TableCell>
              <TableCell align='right'>Active</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {d.assignments.map((a) => (
              <TableRow key={a.id}>
                <TableCell>
                  {a.employeeName}
                  {(a.releasedReason || a.declineReason) && (
                    <Typography variant='caption' display='block' color='text.secondary'>
                      {a.declineReason ?? a.releasedReason}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <StateChip status={a.status} label={a.statusLabel} />
                </TableCell>
                <TableCell>
                  {fmtDateTime(a.assignedAt)}
                  {a.assignedBy && (
                    <Typography variant='caption' display='block' color='text.secondary'>
                      by {a.assignedBy.name}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{fmtDateTime(a.releasedAt ?? a.completedAt)}</TableCell>
                <TableCell align='right'>{fmtHours(a.allocatedHours)}</TableCell>
                <TableCell align='right'>{fmtHours(a.actualHours)}</TableCell>
                <TableCell align='right'>{fmtHours(a.releasedHours)}</TableCell>
                <TableCell align='right'>{fmtHours(a.activeAllocation)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ),
    },
    {
      label: 'Reassignments',
      count: d.reassignments.length,
      body: (
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Requested</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>From → To</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align='right'>Prev. alloc / actual</TableCell>
              <TableCell align='right'>Transferred</TableCell>
              <TableCell>Reason / remarks</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {d.reassignments.map((r) => (
              <TableRow key={r.id}>
                <TableCell>
                  {fmtDateTime(r.requestedAt)}
                  <Typography variant='caption' display='block' color='text.secondary'>
                    by {r.requestedBy?.name ?? '—'}
                    {r.decidedBy && `, decided by ${r.decidedBy.name}`}
                  </Typography>
                </TableCell>
                <TableCell>{humanize(r.requestType)}</TableCell>
                <TableCell>
                  {r.fromEmployee?.name ?? '—'} → {r.toEmployee?.name ?? 'pool'}
                </TableCell>
                <TableCell>
                  <StateChip status={r.status} label={r.statusLabel} />
                  {r.expired && <Typography variant='caption' color='error' display='block'>Offer expired</Typography>}
                </TableCell>
                <TableCell align='right'>
                  {fmtHours(r.previousAllocatedHours)} / {fmtHours(r.previousActualHours)}
                </TableCell>
                <TableCell align='right'>{fmtHours(r.transferredHours)}</TableCell>
                <TableCell>
                  {r.reason}
                  {r.remarks && <Typography variant='caption' display='block' color='text.secondary'>{r.remarks}</Typography>}
                  {r.responseRemarks && <Typography variant='caption' display='block' color='text.secondary'>{r.responseRemarks}</Typography>}
                </TableCell>
              </TableRow>
            ))}
            {d.reassignments.length === 0 && (
              <TableRow><TableCell {...({ colSpan: 7 } as object)} sx={{ color: 'text.secondary' }}>This task has never changed hands.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      ),
    },
    {
      label: 'Daily plan',
      count: d.dailyAllocations.filter((x) => x.active).length,
      body: (
        <Stack gap={1}>
          {can.plan && current && (
            <Button size='small' startIcon={<Icon icon='mdi:calendar-edit' />} sx={{ alignSelf: 'flex-start' }} onClick={() => setDialog('plan')}>
              Edit plan
            </Button>
          )}
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Employee</TableCell>
                <TableCell align='right'>Planned</TableCell>
                <TableCell align='right'>Logged</TableCell>
                <TableCell>State</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {d.dailyAllocations.map((a) => (
                <TableRow key={a.id} sx={{ opacity: a.active ? 1 : 0.5 }}>
                  <TableCell>{fmtDate(a.workDate)}</TableCell>
                  <TableCell>{a.employeeName}</TableCell>
                  <TableCell align='right'>{fmtHours(a.allocatedHours)}</TableCell>
                  <TableCell align='right'>{fmtHours(a.actualHours)}</TableCell>
                  <TableCell>{a.active ? 'Active' : 'Released'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Stack>
      ),
    },
    {
      label: 'Time entries',
      count: d.timeEntries.length,
      body: (
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Employee</TableCell>
              <TableCell>From – to</TableCell>
              <TableCell align='right'>Hours</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Notes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {d.timeEntries.map((e) => (
              <TableRow key={e.id}>
                <TableCell>{fmtDate(e.workDate)}</TableCell>
                <TableCell>{e.employeeName}</TableCell>
                <TableCell>
                  {e.startTime ? fmtDateTime(e.startTime).split(', ')[1] : '—'} – {e.running ? 'running' : e.endTime ? fmtDateTime(e.endTime).split(', ')[1] : '—'}
                </TableCell>
                <TableCell align='right'>{fmtHours(e.hours)}</TableCell>
                <TableCell>{humanize(e.entryType)}</TableCell>
                <TableCell>{e.notes}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ),
    },
    {
      label: 'Dependencies',
      count: d.blockedBy.length + d.blocking.length,
      body: (
        <Stack gap={2}>
          {can.edit && (
            <Button size='small' startIcon={<Icon icon='mdi:link-variant-plus' />} sx={{ alignSelf: 'flex-start' }} onClick={() => setDialog('dependency')}>
              Add dependency
            </Button>
          )}
          {[
            { title: 'Waits on', list: d.blockedBy },
            { title: 'Blocks', list: d.blocking },
          ].map((g) => (
            <Box key={g.title}>
              <Typography variant='subtitle2'>{g.title}</Typography>
              {g.list.length === 0 && <Typography variant='body2' color='text.secondary'>Nothing.</Typography>}
              {g.list.map((x) => (
                <Stack key={x.id} direction='row' alignItems='center' gap={1}>
                  <Icon icon={x.satisfied ? 'mdi:check-circle' : 'mdi:progress-clock'} color={x.satisfied ? 'green' : 'orange'} />
                  <Link component={RouterLink} to={`/tasks/${x.taskId}`}>{x.taskCode}</Link>
                  <Typography variant='body2'>{x.taskTitle}</Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {humanize(x.type)} · {x.taskStatusLabel}
                  </Typography>
                  {can.edit && (
                    <IconButton size='small' aria-label='Remove dependency' onClick={() => act(() => tasksApi.removeDependency(taskId, x.id), 'Dependency removed')}>
                      <Icon icon='mdi:close' />
                    </IconButton>
                  )}
                </Stack>
              ))}
            </Box>
          ))}
        </Stack>
      ),
    },
    {
      label: 'Subtasks',
      count: d.subtasks.length,
      body: (
        <Stack gap={1}>
          {meta.permissions.TASK_CREATE && !['COMPLETED', 'CANCELLED', 'APPROVED'].includes(t.status) && (
            <Button size='small' startIcon={<Icon icon='mdi:plus' />} sx={{ alignSelf: 'flex-start' }} onClick={() => setDialog('subtask')}>
              Add subtask
            </Button>
          )}
          <Table size='small'>
            <TableBody>
              {d.subtasks.map((s) => (
                <SubtaskRow key={s.id} t={s} />
              ))}
            </TableBody>
          </Table>
        </Stack>
      ),
    },
    {
      label: 'Comments',
      count: d.comments.length,
      body: (
        <Stack gap={2}>
          {d.comments.map((c) => (
            <Box key={c.id} sx={{ borderLeft: 3, borderColor: 'primary.light', pl: 2 }}>
              <Stack direction='row' justifyContent='space-between' alignItems='center'>
                <Typography variant='body2' fontWeight={600}>
                  {c.author?.name ?? 'Former user'}{' '}
                  <Typography component='span' variant='caption' color='text.secondary'>
                    {fmtDateTime(c.createdAt)}
                    {c.edited && ' · edited'}
                  </Typography>
                </Typography>
                {(c.mine || can.edit) && (
                  <IconButton size='small' aria-label='Delete comment' onClick={() => act(() => tasksApi.deleteComment(c.id), 'Comment deleted')}>
                    <Icon icon='mdi:delete-outline' />
                  </IconButton>
                )}
              </Stack>
              <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>{c.comment}</Typography>
            </Box>
          ))}
          <Stack direction='row' gap={1}>
            <TextField size='small' fullWidth multiline placeholder='Add a comment' value={comment} onChange={(e) => setComment(e.target.value)} />
            <Button
              variant='contained'
              disabled={!comment.trim() || busy}
              onClick={() => act(async () => { await tasksApi.addComment(taskId, comment); setComment(''); }, 'Comment added')}
            >
              Post
            </Button>
          </Stack>
        </Stack>
      ),
    },
    {
      label: 'Attachments',
      count: d.attachments.length,
      body: (
        <Stack gap={1}>
          <Button component='label' size='small' startIcon={<Icon icon='mdi:paperclip' />} sx={{ alignSelf: 'flex-start' }} disabled={busy}>
            Attach file
            <input hidden type='file' onChange={(e) => onFile(e.target.files?.[0])} />
          </Button>
          {d.attachments.map((a) => (
            <Stack key={a.id} direction='row' alignItems='center' gap={1}>
              <Icon icon='mdi:file-outline' />
              <Link href={a.url} target='_blank' rel='noreferrer'>{a.name}</Link>
              <Typography variant='caption' color='text.secondary'>
                {a.uploadedBy?.name} · {fmtDateTime(a.createdAt)}
              </Typography>
              <IconButton size='small' aria-label='Remove attachment' onClick={() => act(() => tasksApi.detach(taskId, a.id), 'Attachment removed')}>
                <Icon icon='mdi:close' />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      ),
    },
    {
      label: 'Activity',
      count: d.activity.length,
      body: (
        <Stack gap={1.5}>
          {[...d.activity].reverse().map((a) => (
            <Stack key={a.id} direction='row' gap={1.5} alignItems='flex-start'>
              <Box sx={{ color: 'primary.main', pt: 0.25 }}>
                <Icon icon={ACTIVITY_ICONS[a.type] ?? 'mdi:circle-small'} width={20} />
              </Box>
              <Box>
                <Typography variant='body2'>
                  <b>{humanize(a.type.replace(/^TASK_/, ''))}</b>
                  {a.actor && ` · ${a.actor.name}`}
                  {a.fromEmployee && a.toEmployee && ` · ${a.fromEmployee.name} → ${a.toEmployee.name}`}
                  {!a.fromEmployee && a.toEmployee && ` · to ${a.toEmployee.name}`}
                  {a.hours !== null && ` · ${fmtHours(a.hours)}`}
                </Typography>
                {a.message && <Typography variant='body2' color='text.secondary'>{a.message}</Typography>}
                {a.oldValue !== null && a.newValue !== null && !a.message && (
                  <Typography variant='body2' color='text.secondary'>{humanize(a.oldValue)} → {humanize(a.newValue)}</Typography>
                )}
                <Typography variant='caption' color='text.secondary'>{fmtDateTime(a.createdAt)}</Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
      ),
    },
  ];

  const HIDDEN_FOR_EMPLOYEE = ['Assignments', 'Reassignments', 'Daily plan'];
  const tabs = isEmployee ? allTabs.filter((x) => !HIDDEN_FOR_EMPLOYEE.includes(x.label)) : allTabs;
  const shownTab = Math.min(tab, tabs.length - 1);

  return (
    <Stack gap={3}>
      {busy && <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 2000 }} />}
      {/* What the caller has to do right now — first on the page, above the title. */}
      {can.accept && (
        <Alert
          severity='info'
          action={
            <Stack direction='row' gap={1}>
              <Button sx={{ color: 'success.main', fontWeight: 600 }} onClick={() => act(() => tasksApi.accept(taskId), 'Task accepted')}>Accept</Button>
              <Button
                sx={{ color: 'error.main', fontWeight: 600 }}
                onClick={() => setPrompt({ title: 'DECLINE TASK', label: 'Why are you declining?', submitColor: 'error', submitLabel: 'Decline',
                  run: (text) => (offeredToMe && pending ? reassignmentsApi.decline(pending.id, text) : tasksApi.decline(taskId, text)) })}
              >
                Decline
              </Button>
            </Stack>
          }
        >
          {offeredToMe
            ? `${pending?.fromEmployee?.name} is handing this task to you: ${fmtHours(pending?.transferredHours)}. Please answer by ${fmtDate(pending?.expiresAt)}.`
            : `This task is assigned to you (${fmtHours(current?.allocatedHours)}). Accept it to start work.`}
        </Alert>
      )}
      <SectionTitle
        title={`${t.taskCode} · ${t.title}`}
        meta={
          <Stack direction='row' gap={1}>
            <ColorChip label={t.statusLabel} color={t.statusColor} />
            <PriorityChip code={t.priority} label={t.priorityLabel} fallbackColor={t.priorityColor} />
            {t.overdue && <ColorChip label='Overdue' color='#DC2626' />}
          </Stack>
        }
        action={
          <Button size='small' startIcon={<Icon icon='mdi:arrow-left' />} onClick={() => navigate(-1)}>
            Back
          </Button>
        }
      />

      <Card sx={{ py: 2 }}>
        <TaskStatusTrail status={t.status} statusLabel={t.statusLabel} statusColor={t.statusColor} />
      </Card>

      {pending && pending.status === 'REQUESTED' && (
        <Alert
          severity='warning'
          action={
            can.approveReassignment && (
              <Stack direction='row' gap={1}>
                <Button color='inherit' onClick={() => setDialog('approve')}>Review</Button>
                <Button color='inherit' onClick={() => setPrompt({ title: 'REJECT REQUEST', label: 'Remarks', submitColor: 'error', submitLabel: 'Reject',
                  run: (text) => reassignmentsApi.reject(pending.id, text) })}>Reject</Button>
              </Stack>
            )
          }
        >
          {pending.fromEmployee?.name} asked to be released{pending.toEmployee ? ` (suggests ${pending.toEmployee.name})` : ''}: {pending.reason}
        </Alert>
      )}
      {pending && pending.status === 'APPROVED' && !offeredToMe && (
        <Alert severity={pending.expired ? 'error' : 'info'}
          action={can.approveReassignment && (
            <Button color='inherit' onClick={() => setPrompt({ title: 'WITHDRAW OFFER', label: 'Remarks', submitLabel: 'Withdraw',
              run: (text) => reassignmentsApi.cancel(pending.id, text) })}>Withdraw</Button>
          )}>
          Waiting for {pending.toEmployee?.name} to accept ({fmtHours(pending.transferredHours)}){pending.expired ? ' — the offer has expired.' : `, until ${fmtDate(pending.expiresAt)}.`}
        </Alert>
      )}
      {t.status === 'BLOCKED' && d.blockedReason && <Alert severity='error'>Blocked: {d.blockedReason}</Alert>}
      {t.status === 'CANCELLED' && <Alert severity='warning'>Cancelled {fmtDate(d.cancelledAt)}: {d.cancellationReason}</Alert>}

      {/* Actions */}
      <Stack direction='row' gap={1} flexWrap='wrap'>
        {/* An employee restarts work from My Work; here only a first start is offered. */}
        {can.trackTime && !running && !(isEmployee && d.timeEntries.length > 0) && (
          <Button variant='contained' color='success' startIcon={<Icon icon='mdi:play' />} onClick={() => act(() => tasksApi.timer(taskId, d.timeEntries.length ? 'resume' : 'start'), 'Timer started')}>
            {d.timeEntries.length ? 'Resume' : 'Start'}
          </Button>
        )}
        {running && (
          <>
            <Button variant='contained' color='warning' startIcon={<Icon icon='mdi:pause' />} onClick={() => act(() => tasksApi.timer(taskId, 'pause'), 'Paused')}>Pause</Button>
            <Button variant='outlined' color='warning' startIcon={<Icon icon='mdi:stop' />} onClick={() => act(() => tasksApi.timer(taskId, 'stop'), 'Stopped')}>Stop</Button>
          </>
        )}
        {can.trackTime && <Button variant='outlined' startIcon={<Icon icon='mdi:clock-plus-outline' />} onClick={() => setDialog('time')}>Log time</Button>}
        {can.changeStatus && statusOptions.length > 0 && (
          <>
            <Button variant='outlined' endIcon={<Icon icon='mdi:chevron-down' />} onClick={(e) => setStatusMenu(e.currentTarget)}>Status</Button>
            <Menu anchorEl={statusMenu} open={Boolean(statusMenu)} onClose={() => setStatusMenu(null)}>
              {statusOptions.map((o) => (
                <MenuItem key={o.code} onClick={() => chooseStatus(o)}>{o.label}</MenuItem>
              ))}
            </Menu>
          </>
        )}
        {can.requestRelease && (
          <Button variant='outlined' startIcon={<Icon icon='mdi:hand-back-left-outline' />} onClick={() => { setReleaseReason(''); setReleaseTo(null); setDialog('release'); }}>
            Request release
          </Button>
        )}
        {can.assign && <Button variant='outlined' startIcon={<Icon icon='mdi:account-plus-outline' />} onClick={() => setDialog('assign')}>Assign</Button>}
        {can.reassign && <Button variant='outlined' startIcon={<Icon icon='mdi:account-switch-outline' />} onClick={() => setDialog('reassign')}>Reassign</Button>}
        {can.edit && <Button variant='outlined' startIcon={<Icon icon='mdi:pencil-outline' />} onClick={() => setDialog('edit')}>Edit</Button>}
        {can.delete && (
          <Button color='error' startIcon={<Icon icon='mdi:delete-outline' />}
            onClick={() => setPrompt({ title: 'DELETE TASK', message: 'Only a task with no logged time or history can be deleted.', label: 'Type DELETE to confirm', required: true, submitColor: 'error', submitLabel: 'Delete',
              leaves: true,
              run: async (text) => { if (text !== 'DELETE') throw new Error('Type DELETE to confirm'); await tasksApi.remove(taskId); navigate('/tasks'); } })}>
            Delete
          </Button>
        )}
      </Stack>

      {/* Information and hours */}
      <Card sx={{ py: 2 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 2 }}>
          <Fact label='Project'>{t.projectCode} — {t.projectName}</Fact>
          <Fact label='Current employee'>{t.assigneeName}</Fact>
          <Fact label='Work activity'>{t.workActivityName}</Fact>
          <Fact label='Type of work activity'>
            {t.typeOfWorkActivityName}
            {t.requiresStructuralDrawing && (
              <Typography component='span' variant='caption' color='warning.main' display='block'>
                Requires structural drawing
              </Typography>
            )}
          </Fact>
          {t.requiresShutteringFraming && (
            <Fact label='Shuttering / Framing'>{t.structuralWorkType ? humanize(t.structuralWorkType) : null}</Fact>
          )}
          {t.requiresStructuralDrawing && (
            <Fact label={drawingsLabel(t.typeOfWorkActivityName)}>{t.structuralDrawings.length ? t.structuralDrawings.join(', ') : null}</Fact>
          )}
          <Fact label='Created by'>{d.createdBy?.name}</Fact>
          <Fact label='Start'>{fmtDate(t.startDate)}</Fact>
          <Fact label='Due'>{fmtDate(t.dueDate)}</Fact>
          {t.parentTaskId && <Fact label='Parent'><Link component={RouterLink} to={`/tasks/${t.parentTaskId}`}>Open parent task</Link></Fact>}
          <Fact label='Estimated'>{fmtHours(t.estimatedHours)}</Fact>
          <Fact label='Allocated'>{fmtHours(t.allocatedHours)}</Fact>
          <Fact label='Actual'>{fmtHours(t.actualHours)}</Fact>
          <Fact label='Remaining'>{fmtHours(t.remainingHours)}</Fact>
          <Fact label='Completion'>
            <Stack direction='row' alignItems='center' gap={1}>
              <LinearProgress variant='determinate' value={t.completionPercentage} sx={{ flex: 1, height: 6, borderRadius: 3 }} />
              {t.completionPercentage}%
            </Stack>
          </Fact>
        </Box>
        {!isEmployee && (<>
        <Divider />
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 2 }}>
          <Tooltip title='What the current owner still has to do — the only figure that counts as workload'>
            <Box><Fact label='Active allocation'>{fmtHours(d.hours.activeAllocation)}</Fact></Box>
          </Tooltip>
          <Tooltip title='Every allocation ever made on this task, including hours later released and re-issued'>
            <Box><Fact label='Historical allocation'>{fmtHours(d.hours.historicalAllocation)}</Fact></Box>
          </Tooltip>
          <Fact label='Released'>{fmtHours(d.hours.releasedHours)}</Fact>
          <Fact label='Unassigned'>{fmtHours(d.hours.unassignedHours)}</Fact>
        </Box>
        </>)}
        {d.description && (
          <>
            <Divider />
            <Typography sx={{ whiteSpace: 'pre-wrap' }}>{d.description}</Typography>
          </>
        )}
      </Card>

      <Card sx={{ py: 1 }}>
        <Tabs value={shownTab} onChange={(_, v) => setTab(v)} variant='scrollable' scrollButtons='auto'>
          {tabs.map((x) => (
            <Tab key={x.label} label={x.count ? `${x.label} (${x.count})` : x.label} />
          ))}
        </Tabs>
        <Box sx={{ overflowX: 'auto' }}>{tabs[shownTab].body}</Box>
      </Card>

      {/* Dialogs */}
      <TaskFormDialog open={dialog === 'edit'} meta={meta} task={d} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />
      <TaskFormDialog open={dialog === 'subtask'} meta={meta} parentTaskId={taskId} defaultProjectId={t.projectId} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />
      {dialog === 'assign' && <AssignDialog open meta={meta} detail={d} onClose={() => setDialog(null)} onDone={() => { setDialog(null); load(); }} />}
      {(dialog === 'reassign' || dialog === 'approve') && (
        <ReassignDialog open meta={meta} context={reassignContext} request={dialog === 'approve' ? pending : null}
          canSkipAcceptance={can.reassignWithoutAcceptance} onClose={() => setDialog(null)} onDone={() => { setDialog(null); load(); }} />
      )}
      {dialog === 'plan' && current && (
        <PlanDialog open assignment={current} rows={d.dailyAllocations} onClose={() => setDialog(null)} onDone={() => { setDialog(null); load(); }} />
      )}
      <TimeEntryDialog open={dialog === 'time'} tasks={[{ id: taskId, label: `${t.taskCode} · ${t.title}` }]} taskId={taskId}
        onClose={() => setDialog(null)} onDone={() => { setDialog(null); load(); }} />
      <DialogShell open={dialog === 'release'} title='REQUEST RELEASE' onClose={() => setDialog(null)} submitLabel='Send request' disabled={!releaseReason.trim()}
        onSubmit={() => act(async () => { await tasksApi.requestRelease(taskId, releaseReason, releaseTo); setDialog(null); }, 'Release requested')}>
        <Typography variant='body2' color='text.secondary'>
          A manager will review this. Your {fmtHours(current?.actualHours)} of logged work stays recorded against you; the {fmtHours(current?.remainingHours)} left moves to whoever takes over.
        </Typography>
        <TextField label='Reason' size='small' required multiline minRows={2} value={releaseReason} onChange={(e) => setReleaseReason(e.target.value)} />
        <EmployeePicker employees={meta.employees} value={releaseTo} onChange={setReleaseTo} label='Suggest who could take over (optional)' exclude={meta.myEmployeeId} />
      </DialogShell>
      <DialogShell open={dialog === 'dependency'} title='ADD DEPENDENCY' onClose={() => setDialog(null)} submitLabel='Add' disabled={!dep.related}
        onSubmit={() => act(async () => { await tasksApi.addDependency(taskId, Number(dep.related), dep.type); setDialog(null); }, 'Dependency added')}>
        <TextField select size='small' label='Relation' value={dep.type} onChange={(e) => setDep((s) => ({ ...s, type: e.target.value }))}>
          <MenuItem value='BLOCKED_BY'>This task is blocked by…</MenuItem>
          <MenuItem value='BLOCKS'>This task blocks…</MenuItem>
          <MenuItem value='DEPENDS_ON'>This task depends on (advisory)…</MenuItem>
        </TextField>
        <TextField size='small' label='Other task id' type='number' helperText='The number in TASK-0042 is 42' value={dep.related} onChange={(e) => setDep((s) => ({ ...s, related: e.target.value }))} />
      </DialogShell>
      <DialogShell open={confirmSubmit} title='SUBMIT FOR REVIEW' onClose={() => setConfirmSubmit(false)} submitLabel='Yes, submit'
        onSubmit={() => act(async () => { await tasksApi.changeStatus(taskId, 'SUBMITTED'); setConfirmSubmit(false); }, 'Submitted for review')}>
        <Typography variant='body2'>
          Submit <b>{t.title}</b> for review? The work goes to the analyst to check, and the timer cannot be restarted on it
          until a revision is requested.
        </Typography>
      </DialogShell>
      {prompt && (
        <TextPromptDialog open title={prompt.title} message={prompt.message} label={prompt.label} required={prompt.required}
          submitLabel={prompt.submitLabel} submitColor={prompt.submitColor} onClose={() => setPrompt(null)}
          onSubmit={async (text) => { await act(() => prompt.run(text), 'Done', !prompt.leaves); setPrompt(null); }} />
      )}
    </Stack>
  );
}
